import React, { useState, useContext } from 'react';
import {
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  Divider
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  AreaChart,
  Area
} from 'recharts';
import { useQuery } from '@apollo/client';
import { gql } from '@apollo/client';
import AuthContext from '../contexts/AuthContext';

// Requête GraphQL pour récupérer toutes les demandes d'absences
const GET_ABSENCE_REQUESTS = gql`
  query GetAbsenceRequests($employeeId: ID, $status: String, $startDate: Date, $endDate: Date, $departmentId: ID) {
    absenceRequests(employeeId: $employeeId, status: $status, startDate: $startDate, endDate: $endDate, departmentId: $departmentId) {
      id
      employee {
        id
        firstName
        lastName
        department {
          id
          name
        }
      }
      absenceType {
        id
        code
        name
      }
      startDate
      endDate
      durationDays
      status
      reason
      createdAt
    }
  }
`;

// Requête pour récupérer les départements
const GET_DEPARTMENTS = gql`
  query GetDepartments {
    departments {
      id
      name
    }
  }
`;

// Requête pour récupérer le nombre d'employés par département
const GET_EMPLOYEES_COUNT = gql`
  query GetEmployeesCount($departmentId: ID) {
    users(role: null, departmentId: $departmentId) {
      id
      role
      department {
        id
        name
      }
    }
  }
`;

// Couleurs pour les graphiques
const COLORS = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#FFC658', '#FF8E53', '#A8E6CF', '#DCEDC8', '#FFD3B6', '#FFAAA5', '#FF8B94'];

function AbsenceStats() {
  const { user } = useContext(AuthContext);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedDepartment, setSelectedDepartment] = useState('');

  // Calculer les dates de début et fin pour le mois sélectionné
  const startDate = new Date(selectedYear, selectedMonth - 1, 1).toISOString().split('T')[0];
  const endDate = new Date(selectedYear, selectedMonth, 0).toISOString().split('T')[0];

  // Requêtes GraphQL
  const { data: absenceRequestsData, loading: requestsLoading, error: requestsError } = useQuery(GET_ABSENCE_REQUESTS, {
    variables: {
      employeeId: null,
      status: 'APPROVED',
      startDate: startDate,
      endDate: endDate,
      departmentId: selectedDepartment || null
    }
  });

  const { data: departmentsData } = useQuery(GET_DEPARTMENTS);
  
  const { data: employeesData } = useQuery(GET_EMPLOYEES_COUNT, {
    variables: {
      departmentId: selectedDepartment || null
    }
  });

  // Calculer les statistiques avancées
  const calculateAdvancedStats = () => {
    if (!absenceRequestsData?.absenceRequests) return { departments: [], totals: {}, trends: [] };

    const requests = absenceRequestsData.absenceRequests;
    const departmentStats = {};
    const totalStats = {
      totalAbsences: 0,
      totalEmployees: new Set(),
      employeesWithAbsences: new Set(),
      sickLeave: 0,
      vacation: 0,
      mission: 0,
      paternity: 0,
      casual: 0,
      compassionate: 0,
      workFromHome: 0,
      compOff: 0,
      halfDay1: 0,
      halfDay2: 0,
      averageAbsenceDuration: 0,
      longestAbsence: 0,
      mostCommonType: '',
      typeCounts: {}
    };

    // Compter le total d'employés par département
    const departmentEmployeeCounts = {};
    if (employeesData?.users) {
      employeesData.users.forEach(employee => {
        if (['RH', 'HOST', 'EMPLOYEE', 'MANAGER'].includes(employee.role)) {
          const deptName = employee.department?.name || 'Sans département';
          if (!departmentEmployeeCounts[deptName]) {
            departmentEmployeeCounts[deptName] = 0;
          }
          departmentEmployeeCounts[deptName]++;
          totalStats.totalEmployees.add(employee.id);
        }
      });
    }

    requests.forEach(request => {
      const deptName = request.employee.department?.name || 'Sans département';
      const absenceType = request.absenceType?.code || 'UNKNOWN';
      const duration = parseFloat(request.durationDays) || 0;

      // Initialiser les stats du département
      if (!departmentStats[deptName]) {
        departmentStats[deptName] = {
          department: deptName,
          totalAbsences: 0,
          totalEmployees: departmentEmployeeCounts[deptName] || 0,
          employeesWithAbsences: new Set(),
          sickLeave: 0,
          vacation: 0,
          mission: 0,
          paternity: 0,
          casual: 0,
          compassionate: 0,
          workFromHome: 0,
          compOff: 0,
          halfDay1: 0,
          halfDay2: 0,
          averageDuration: 0,
          longestAbsence: 0,
          typeCounts: {}
        };
      }

      // Ajouter l'employé aux statistiques
      departmentStats[deptName].employeesWithAbsences.add(request.employee.id);
      totalStats.employeesWithAbsences.add(request.employee.id);

      // Compter par type
      const typeName = getAbsenceTypeName(absenceType);
      if (!totalStats.typeCounts[typeName]) totalStats.typeCounts[typeName] = 0;
      if (!departmentStats[deptName].typeCounts[typeName]) departmentStats[deptName].typeCounts[typeName] = 0;
      
      totalStats.typeCounts[typeName]++;
      departmentStats[deptName].typeCounts[typeName]++;

      // Mettre à jour les statistiques par type
      switch (absenceType) {
        case 'S':
          departmentStats[deptName].sickLeave += duration;
          totalStats.sickLeave += duration;
          break;
        case 'V':
          departmentStats[deptName].vacation += duration;
          totalStats.vacation += duration;
          break;
        case 'M':
          departmentStats[deptName].mission += duration;
          totalStats.mission += duration;
          break;
        case 'P':
          departmentStats[deptName].paternity += duration;
          totalStats.paternity += duration;
          break;
        case 'C':
          departmentStats[deptName].casual += duration;
          totalStats.casual += duration;
          break;
        case 'Co':
          departmentStats[deptName].compassionate += duration;
          totalStats.compassionate += duration;
          break;
        case 'W':
          departmentStats[deptName].workFromHome += duration;
          totalStats.workFromHome += duration;
          break;
        case 'O':
          departmentStats[deptName].compOff += duration;
          totalStats.compOff += duration;
          break;
        case 'H1':
          departmentStats[deptName].halfDay1 += duration;
          totalStats.halfDay1 += duration;
          break;
        case 'H2':
          departmentStats[deptName].halfDay2 += duration;
          totalStats.halfDay2 += duration;
          break;
        default:
          departmentStats[deptName].casual += duration;
          totalStats.casual += duration;
      }

      departmentStats[deptName].totalAbsences += duration;
      totalStats.totalAbsences += duration;

      // Mettre à jour les durées
      if (duration > departmentStats[deptName].longestAbsence) {
        departmentStats[deptName].longestAbsence = duration;
      }
      if (duration > totalStats.longestAbsence) {
        totalStats.longestAbsence = duration;
      }
    });

    // Calculer les moyennes et trouver le type le plus commun
    const departments = Object.values(departmentStats).map(dept => {
      const avgDuration = dept.employeesWithAbsences.size > 0 ? dept.totalAbsences / dept.employeesWithAbsences.size : 0;
      const mostCommonType = Object.keys(dept.typeCounts).reduce((a, b) => dept.typeCounts[a] > dept.typeCounts[b] ? a : b, '');
      
      return {
        ...dept,
        employeesWithAbsences: dept.employeesWithAbsences.size,
        totalAbsences: Math.round(dept.totalAbsences * 100) / 100,
        averageDuration: Math.round(avgDuration * 100) / 100,
        mostCommonType,
        sickLeave: Math.round(dept.sickLeave * 100) / 100,
        vacation: Math.round(dept.vacation * 100) / 100,
        mission: Math.round(dept.mission * 100) / 100,
        paternity: Math.round(dept.paternity * 100) / 100,
        casual: Math.round(dept.casual * 100) / 100,
        compassionate: Math.round(dept.compassionate * 100) / 100,
        workFromHome: Math.round(dept.workFromHome * 100) / 100,
        compOff: Math.round(dept.compOff * 100) / 100,
        halfDay1: Math.round(dept.halfDay1 * 100) / 100,
        halfDay2: Math.round(dept.halfDay2 * 100) / 100,
      };
    });

    // Calculer les totaux et moyennes globales
    const avgDuration = totalStats.employeesWithAbsences.size > 0 ? totalStats.totalAbsences / totalStats.employeesWithAbsences.size : 0;
    const mostCommonType = Object.keys(totalStats.typeCounts).reduce((a, b) => totalStats.typeCounts[a] > totalStats.typeCounts[b] ? a : b, '');

    const totals = {
      ...totalStats,
      totalEmployees: totalStats.totalEmployees.size,
      employeesWithAbsences: totalStats.employeesWithAbsences.size,
      totalAbsences: Math.round(totalStats.totalAbsences * 100) / 100,
      averageAbsenceDuration: Math.round(avgDuration * 100) / 100,
      mostCommonType,
      longestAbsence: Math.round(totalStats.longestAbsence * 100) / 100,
      sickLeave: Math.round(totalStats.sickLeave * 100) / 100,
      vacation: Math.round(totalStats.vacation * 100) / 100,
      mission: Math.round(totalStats.mission * 100) / 100,
      paternity: Math.round(totalStats.paternity * 100) / 100,
      casual: Math.round(totalStats.casual * 100) / 100,
      compassionate: Math.round(totalStats.compassionate * 100) / 100,
      workFromHome: Math.round(totalStats.workFromHome * 100) / 100,
      compOff: Math.round(totalStats.compOff * 100) / 100,
      halfDay1: Math.round(totalStats.halfDay1 * 100) / 100,
      halfDay2: Math.round(totalStats.halfDay2 * 100) / 100,
    };

    // Créer des données de tendance (simulation pour l'exemple)
    const trends = [
      { month: 'Jan', absences: Math.round(totals.totalAbsences * 0.8) },
      { month: 'Fév', absences: Math.round(totals.totalAbsences * 0.9) },
      { month: 'Mar', absences: Math.round(totals.totalAbsences * 1.1) },
      { month: 'Avr', absences: Math.round(totals.totalAbsences * 0.95) },
      { month: 'Mai', absences: Math.round(totals.totalAbsences * 1.05) },
      { month: 'Juin', absences: Math.round(totals.totalAbsences * 1.2) },
    ];

    return { departments, totals, trends };
  };

  const getAbsenceTypeName = (code) => {
    const types = {
      'S': 'Congé Maladie',
      'V': 'Vacances',
      'M': 'Mission',
      'P': 'Congé Paternité',
      'C': 'Congé Occasionnel',
      'Co': 'Congé Compassion',
      'W': 'Télétravail',
      'O': 'Compensation',
      'H1': 'Demi-journée 1',
      'H2': 'Demi-journée 2'
    };
    return types[code] || 'Autre';
  };

  const preparePieData = (totals) => {
    if (!totals?.typeCounts) return [];

    return Object.entries(totals.typeCounts).map(([name, value]) => ({
      name,
      value
    })).filter(item => item.value > 0);
  };

  const prepareBarData = (departments) => {
    return departments.map(dept => ({
      department: dept.department,
      'Taux d\'absence': dept.totalEmployees > 0 ? Math.round((dept.employeesWithAbsences / dept.totalEmployees) * 100) : 0,
      'Durée moyenne': dept.averageDuration,
      'Absences totales': dept.totalAbsences
    }));
  };

  if (requestsLoading) return <CircularProgress sx={{ m: 4 }} />;
  if (requestsError) return <Alert severity="error">Erreur: {requestsError.message}</Alert>;

  const { departments: chartData, totals, trends } = calculateAdvancedStats();
  const pieData = preparePieData(totals);
  const barData = prepareBarData(chartData);

  const absenceRate = totals?.totalEmployees > 0 ? (totals?.employeesWithAbsences / totals?.totalEmployees) * 100 : 0;
  const productivityImpact = Math.round((absenceRate / 100) * 25); // Impact estimé sur la productivité

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold', color: '#2c3e50' }}>
        📊 Tableau de Bord des Absences
      </Typography>
      <Typography variant="subtitle1" sx={{ color: '#7f8c8d', mb: 3 }}>
        Analyse complète des absences pour {new Date(selectedYear, selectedMonth - 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
      </Typography>
      
      {/* Filtres */}
      <Paper sx={{ p: 3, mb: 4, borderRadius: 2, boxShadow: 2 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>📅 Année</InputLabel>
              <Select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                label="📅 Année"
              >
                {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(year => (
                  <MenuItem key={year} value={year}>{year}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>🗓️ Mois</InputLabel>
              <Select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                label="🗓️ Mois"
              >
                {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                  <MenuItem key={month} value={month}>
                    {new Date(2024, month - 1).toLocaleDateString('fr-FR', { month: 'long' })}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>🏢 Département</InputLabel>
              <Select
                value={selectedDepartment}
                onChange={(e) => setSelectedDepartment(e.target.value)}
                label="🏢 Département"
              >
                <MenuItem value="">Tous les départements</MenuItem>
                {departmentsData?.departments?.map(dept => (
                  <MenuItem key={dept.id} value={dept.id}>
                    {dept.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Métriques principales */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ borderRadius: 3, boxShadow: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            <CardContent sx={{ color: 'white', textAlign: 'center' }}>
              <Typography variant="h2" sx={{ fontWeight: 'bold', mb: 1 }}>
                {totals?.totalAbsences || 0}
              </Typography>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Jours d'absences
              </Typography>
              <Typography variant="body2">
                {totals?.employeesWithAbsences || 0} employés concernés
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card sx={{ borderRadius: 3, boxShadow: 3, background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' }}>
            <CardContent sx={{ color: 'white', textAlign: 'center' }}>
              <Typography variant="h2" sx={{ fontWeight: 'bold', mb: 1 }}>
                {totals?.totalEmployees || 0}
              </Typography>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Effectif total
              </Typography>
              <Typography variant="body2">
                {totals?.employeesWithAbsences || 0} avec absences
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card sx={{ borderRadius: 3, boxShadow: 3, background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)' }}>
            <CardContent sx={{ color: 'white', textAlign: 'center' }}>
              <Typography variant="h2" sx={{ fontWeight: 'bold', mb: 1 }}>
                {absenceRate.toFixed(1)}%
              </Typography>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Taux d'absence
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={Math.min(absenceRate, 100)} 
                sx={{ 
                  height: 8, 
                  borderRadius: 4, 
                  backgroundColor: 'rgba(255,255,255,0.3)',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: 'white'
                  }
                }} 
              />
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card sx={{ borderRadius: 3, boxShadow: 3, background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' }}>
            <CardContent sx={{ color: 'white', textAlign: 'center' }}>
              <Typography variant="h2" sx={{ fontWeight: 'bold', mb: 1 }}>
                {totals?.averageAbsenceDuration || 0}
              </Typography>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Durée moyenne
              </Typography>
              <Typography variant="body2">
                {totals?.longestAbsence || 0} jours max
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Graphiques */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Tendance des absences */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 2 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: '#2c3e50' }}>
              📈 Évolution des absences
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={trends}>
                <CartesianGrid strokeDasharray="3 3" stroke="#ecf0f1" />
                <XAxis dataKey="month" stroke="#7f8c8d" />
                <YAxis stroke="#7f8c8d" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#fff', 
                    border: 'none', 
                    borderRadius: 8, 
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)' 
                  }}
                />
                <Area 
                  type="monotone" 
                  dataKey="absences" 
                  stroke="#667eea" 
                  fill="#667eea" 
                  fillOpacity={0.3}
                />
              </AreaChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Répartition par type */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 2 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: '#2c3e50' }}>
              🥧 Types d'absences
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#fff', 
                    border: 'none', 
                    borderRadius: 8, 
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)' 
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
            <Box sx={{ mt: 2 }}>
              {pieData.map((item, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Box 
                    sx={{ 
                      width: 12, 
                      height: 12, 
                      borderRadius: '50%', 
                      backgroundColor: COLORS[index % COLORS.length],
                      mr: 1 
                    }} 
                  />
                  <Typography variant="body2" sx={{ flex: 1 }}>{item.name}</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{item.value}</Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Comparaison par département */}
      <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 2, mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: '#2c3e50' }}>
          🏢 Performance du département
        </Typography>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={barData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#ecf0f1" />
            <XAxis dataKey="department" stroke="#7f8c8d" />
            <YAxis stroke="#7f8c8d" />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: '#fff', 
                border: 'none', 
                borderRadius: 8, 
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)' 
              }}
            />
            <Legend />
            <Bar dataKey="Taux d'absence" fill="#e74c3c" radius={[4, 4, 0, 0]} />
            <Bar dataKey="Durée moyenne" fill="#3498db" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Paper>

      {/* Tableau détaillé */}
      <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 2 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: '#2c3e50' }}>
          📋 Détail complet du département
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Département</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Effectif</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Absences</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Taux</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Durée moy.</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Type principal</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Jours total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {chartData.map((row) => (
                <TableRow key={row.department} hover>
                  <TableCell sx={{ fontWeight: 'bold' }}>{row.department}</TableCell>
                  <TableCell align="right">
                    <Chip 
                      label={row.totalEmployees} 
                      color="primary" 
                      size="small" 
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Chip 
                      label={row.employeesWithAbsences} 
                      color="secondary" 
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                      <Typography variant="body2" sx={{ mr: 1 }}>
                        {row.totalEmployees > 0 ? `${((row.employeesWithAbsences / row.totalEmployees) * 100).toFixed(1)}%` : '0%'}
                      </Typography>
                      <LinearProgress 
                        variant="determinate" 
                        value={row.totalEmployees > 0 ? (row.employeesWithAbsences / row.totalEmployees) * 100 : 0} 
                        sx={{ width: 60, height: 6, borderRadius: 3 }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {row.averageDuration} jours
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Chip 
                      label={row.mostCommonType} 
                      color="info" 
                      size="small" 
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#e74c3c' }}>
                      {row.totalAbsences}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Insights et recommandations */}
      <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 2, mt: 4, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'white' }}>
          💡 Insights et recommandations
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Box sx={{ color: 'white' }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                🎯 Objectif atteint
              </Typography>
              <Typography variant="body2">
                {absenceRate < 15 ? 'Excellent taux d\'absence !' : 
                 absenceRate < 25 ? 'Taux d\'absence acceptable' : 
                 'Taux d\'absence à surveiller'}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ color: 'white' }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                📊 Impact productivité
              </Typography>
              <Typography variant="body2">
                Impact estimé : {productivityImpact}% sur la productivité
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ color: 'white' }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                🔍 Type dominant
              </Typography>
              <Typography variant="body2">
                {totals?.mostCommonType || 'Aucun'} : type d'absence le plus fréquent
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
}

export default AbsenceStats; 